import React from 'react';
import { Box, Card, CardContent, Container, Grid, Typography } from '@material-ui/core';

const HowItWorksTimeline = ({ sectionConfig = {}, layoutContext = {} }) => {
    const {
        applyHoverEffect,
        removeHoverEffect,
        getIcon,
        hoveredHowItWorksIndex,
        setHoveredHowItWorksIndex,
        getAnimationProps
    } = layoutContext;

    const items = sectionConfig.items || [];
    if (!items.length) {
        return null;
    }

    const handleHover = (index) => setHoveredHowItWorksIndex?.(index);
    const clearHover = () => setHoveredHowItWorksIndex?.(null);

    const sectionLabel = sectionConfig.label || sectionConfig.defaultLabel || 'How It Works';
    const applyAnimation = (animationConfig, index = 0) => (
        typeof getAnimationProps === 'function' ? getAnimationProps(animationConfig, index) : {}
    );
    const sectionAnimation = applyAnimation(sectionConfig.animation?.section);
    const titleAnimation = applyAnimation(sectionConfig.animation?.title);

    return (
        <Box
            id="howitworks-section"
            className={sectionAnimation.className}
            data-anim={sectionAnimation.dataAnim}
            data-delay={sectionAnimation.dataDelay}
            data-trigger-once={sectionAnimation.dataTriggerOnce}
            style={{ ...(sectionConfig.styles || {}), ...(sectionAnimation.style || {}) }}
        >
            <Container maxWidth={sectionConfig.container?.maxWidth || 'lg'} style={sectionConfig.containerStyles || {}}>
                <Box
                    className={titleAnimation.className}
                    data-anim={titleAnimation.dataAnim}
                    data-delay={titleAnimation.dataDelay}
                    data-trigger-once={titleAnimation.dataTriggerOnce}
                    style={{ ...(sectionConfig.titleContainerStyles || {}), ...(titleAnimation.style || {}) }}
                >
                    {sectionLabel && (
                        <Typography variant={sectionConfig.typography?.label?.variant || 'body2'} style={sectionConfig.labelStyles || {}}>
                            {sectionLabel}
                        </Typography>
                    )}
                    <Typography variant={sectionConfig.typography?.title?.variant || 'h3'} style={sectionConfig.titleStyles || {}}>
                        {sectionConfig.title}
                    </Typography>
                    {sectionConfig.subtitle && (
                        <Typography variant={sectionConfig.typography?.subtitle?.variant || 'body1'} style={sectionConfig.subtitleStyles || {}}>
                            {sectionConfig.subtitle}
                        </Typography>
                    )}
                </Box>
                <Grid container spacing={sectionConfig.grid?.spacing || 4}>
                    {items.map((step, index) => {
                        const cardAnimation = applyAnimation(sectionConfig.animation?.items, index);
                        const isHovered = hoveredHowItWorksIndex === index;
                        return (
                            <Grid
                                item
                                xs={sectionConfig.gridItem?.xs || 12}
                                sm={sectionConfig.gridItem?.sm || 6}
                                md={sectionConfig.gridItem?.md || 3}
                                key={`howitworks-${index}`}
                            >
                                <Card
                                    className={cardAnimation.className}
                                    data-anim={cardAnimation.dataAnim}
                                    data-delay={cardAnimation.dataDelay}
                                    data-trigger-once={cardAnimation.dataTriggerOnce}
                                    style={{
                                        ...(sectionConfig.cardStyles || {}),
                                        ...(isHovered ? sectionConfig.cardHoverStyles || {} : {}),
                                        ...(cardAnimation.style || {})
                                    }}
                                    onMouseEnter={(event) => {
                                        handleHover(index);
                                        applyHoverEffect?.(event, sectionConfig.cardHoverStyles || {});
                                    }}
                                    onMouseLeave={(event) => {
                                        clearHover();
                                        removeHoverEffect?.(event, sectionConfig.cardStyles || {});
                                    }}
                                >
                                    {/* Background Number */}
                                    <Box
                                        style={{
                                            position: 'absolute',
                                            top: '-0.5rem',
                                            left: '0.5rem',
                                            fontSize: '6.5rem',
                                            fontWeight: 900,
                                            color: '#003B5C',
                                            opacity: isHovered ? 0.15 : 0.08,
                                            lineHeight: 1,
                                            pointerEvents: 'none',
                                            transition: 'opacity 0.3s ease, color 0.3s ease',
                                            zIndex: 0,
                                            ...(sectionConfig.backgroundNumberStyles || {})
                                        }}
                                    >
                                        {step.number || String(index + 1).padStart(2, '0')}
                                    </Box>
                                    <CardContent style={{ ...(sectionConfig.cardContentStyles || {}), position: 'relative', zIndex: 1, paddingTop: '4rem' }}>
                                        {step.icon && (
                                            <Box
                                                style={{
                                                    ...(sectionConfig.iconBoxStyles || {}),
                                                    ...(isHovered ? sectionConfig.iconBoxHoverStyles || {} : {})
                                                }}
                                            >
                                                {getIcon?.(step.icon, {
                                                    style: {
                                                        ...(sectionConfig.iconStyles || {}),
                                                        ...(isHovered ? sectionConfig.iconHoverStyles || {} : {})
                                                    }
                                                })}
                                            </Box>
                                        )}
                                        <Typography
                                            variant={sectionConfig.typography?.cardTitle?.variant || 'h6'}
                                            style={{
                                                ...(sectionConfig.cardTitleStyles || {}),
                                                ...(isHovered ? sectionConfig.cardTitleHoverStyles || {} : {})
                                            }}
                                        >
                                            {step.title}
                                        </Typography>
                                        <Typography
                                            variant={sectionConfig.typography?.cardDescription?.variant || 'body2'}
                                            style={{
                                                ...(sectionConfig.cardDescriptionStyles || {}),
                                                ...(isHovered ? sectionConfig.cardDescriptionHoverStyles || {} : {})
                                            }}
                                        >
                                            {step.description}
                                        </Typography>
                                        {/* Divider line at bottom */}
                                        <Box
                                            style={{
                                                ...(sectionConfig.dividerStyles || {}),
                                                ...(isHovered ? sectionConfig.dividerHoverStyles || {} : {})
                                            }}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </Container>
        </Box>
    );
};

export default HowItWorksTimeline;
